<script setup lang="ts">
import { computed } from 'vue';
import { useTaskHistory } from '@/composables/tasks/useTaskHistory';
import ModalBox from "@/components/v2/Modal/ModalBox.vue";

const taskHistory = useTaskHistory();

const getValueDisplay = (value: unknown): string => {
  if (value === null || value === undefined) return 'None';

  if (typeof value === 'object' && value !== null) {
    if (value.value) {
      return value.value;
    }

    const obj = value as Record<string, unknown>;
    return (obj.label as string) || (obj.name as string) || (obj.title as string) || JSON.stringify(value);
  }

  return String(value);
};

const handleClose = () => {
  taskHistory.cancelJustification();
};

const isEndingBathProcessEarly = computed(() => {
  return taskHistory.justificationModal.fieldName.includes('Ending Bath Process Early');
});

</script>

<template>
  <ModalBox v-if="taskHistory.justificationModal.isOpen" @close="handleClose">
    <template #header>Justification Required</template>

    <template #main>
      <div v-if="isEndingBathProcessEarly" class="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <div class="flex items-start gap-3">
          <svg class="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <div>
            <h3 class="text-sm font-semibold text-orange-800 mb-1">Ending Bath Process Early</h3>
            <p class="text-sm text-orange-700">
              You are about to end the bath process before the expected completion time. 
              This action requires justification. Please provide a detailed reason for ending the process early.
            </p>
          </div>
        </div>
      </div>

      <p class="mb-4 text-sm">
        <template v-if="!isEndingBathProcessEarly">
          Please provide a reason for changing
          <strong>{{ taskHistory.justificationModal.fieldName }}</strong>.
        </template>
        <template v-else>
          <strong>{{ taskHistory.justificationModal.fieldName }}</strong>
        </template>
      </p>

      <div class="mb-4">
        <div class="text-sm text-gray-600 mb-1">From:</div>
        <div class="p-2 bg-gray-50 rounded">
          {{ getValueDisplay(taskHistory.justificationModal.oldValue) }}
        </div>
      </div>

      <div class="mb-4">
        <div class="text-sm text-gray-600 mb-1">To:</div>
        <div class="p-2 bg-gray-50 rounded">
          {{ getValueDisplay(taskHistory.justificationModal.newValue) }}
        </div>
      </div>

      <textarea
          v-model="taskHistory.justificationModal.justification"
          class="w-full border rounded p-2 mb-4"
          rows="3"
          :placeholder="isEndingBathProcessEarly ? 'Please explain why you are ending the bath process early...' : 'Enter justification...'"
      ></textarea>
    </template>

    <template #footer>
      <div class="flex justify-end gap-2">
        <button
            class="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10"
            @click="handleClose"
        >
          Cancel
        </button>
        <button
            class="text-white bg-primary hover:bg-primary-dark focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:z-10"
            @click="taskHistory.submitJustification()"
            :disabled="!taskHistory.justificationModal.justification.trim()"
        >
          Confirm
        </button>
      </div>
    </template>
  </ModalBox>
</template>
